import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckIcon from '@mui/icons-material/Check'
import LocalParkingIcon from '@mui/icons-material/LocalParking'
import { placesDataHead } from '../../../../utils/stub'

import TableHeader from './table-header'
import {
  useGetParkingListQuery,
  useParkingDeleteMutation,
  useParkingListUpdateMutation,
  useParkingFreeSpaceMutation
} from './api-slice'
import DialogForm from './dialog-form'
import { useSelector } from 'react-redux'
import { selectActiveBuildingId } from '@/features/building/buildingSlice'
import WidgetPaper from '@/components/WidgetPaper'
import TableEmptyPlaceholder from '@/components/TableEmptyPlaceholder'
import SearchInput from '@/components/SearchInput'
import AddButton from '../access/AddButton'

const ParkingPlaces = ({ ref }) => {
  const activeBuildingId = useSelector(selectActiveBuildingId);
  const { t } = useTranslation()
  const [handleDelete] = useParkingDeleteMutation()
  const [updateList, { isSuccess: updateSuccess, isLoading: updateLoading }] =
    useParkingListUpdateMutation()
  const [orderBy, setOrderBy] = useState('id')
  const [order, setOrder] = useState('asc')
  const [selected, setSelected] = useState([])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [apiPage, setApiPage] = useState(1)
  const [open, setOpen] = useState(false)
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentId, setCurrentId] = useState()
  const [deleteModal, setDeleteModal] = useState(false)
  const [checkOpen, setCheckOpen] = useState(false)
  const [tabId, setTabId] = useState(1)
  const [approveId, setApproveId] = useState(null)
  const [freeSpaceOpen, setFreeSpaceOpen] = useState(false)
  const [freeSpaceId, setFreeSpaceId] = useState(null)
  const [freeSpace, { isLoading: isFreeSpaceLoading }] =
    useParkingFreeSpaceMutation()
  const {
    data: parkingLists,
    isSuccess,
    isLoading
  } = useGetParkingListQuery({
    buildingId: activeBuildingId,
    page: apiPage,
    size: rowsPerPage,
    active: tabId === 1
  })
  const emptyPlaceholder = `${t('list_empty')}\n${t('parking-places-list_explained')}`

  const handleFreeSpace = (id) => () => {
    setFreeSpaceId(id)
    setFreeSpaceOpen(true)
  }

  // Функция для подтверждения освобождения места
  const confirmFreeSpace = async () => {
    try {
      await freeSpace({ parkingId: freeSpaceId }).unwrap()
      setFreeSpaceOpen(false)
      setFreeSpaceId(null)
    } catch (error) {
      console.error('Error freeing parking space:', error)
    }
  }

  const cancelFreeSpace = () => {
    setFreeSpaceOpen(false)
    setFreeSpaceId(null)
  }

  const currentList = parkingLists?.data?.find((item) => item.id === approveId)

  const lists = parkingLists?.data
    ?.filter((list) => {
      if (!search.trim()) return true

      const searchTerm = search.trim().toLowerCase()

      // Поиск по номеру паркоместа - преобразуем в строку для безопасного поиска
      const parkingNumberMatch = String(list.number || '')
        .toLowerCase()
        .includes(searchTerm)

      // Поиск по номеру телефона
      const phoneMatch = String(list.phone || '')
        .replace(/[^0-9]/g, '')
        .includes(searchTerm.replace(/[^0-9]/g, ''))

      // Поиск по номеру автомобиля
      const carNumberMatch = String(list.car_number || '')
        .toLowerCase()
        .includes(searchTerm)

      return parkingNumberMatch || phoneMatch || carNumberMatch
    })
    ?.filter((list) => (tabId === 1 ? list.is_approved : !list.is_approved))
    ?? []

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = lists?.map((item) => item.number)
      setSelected(newSelecteds)
      return
    }
    setSelected([])
  }

  const handleOpenModal = () => {
    setOpen(true)
  }

  const handleCloseModal = () => {
    setOpen(false)
    setCurrentId(null)
  }

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name)
    let newSelected = []
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      )
    }
    setSelected(newSelected)
  }

  const handleCheck = (id) => () => {
    setApproveId(id)
    setCheckOpen(true)
  }

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1
    }
    if (b[orderBy] > a[orderBy]) {
      return 1
    }
    return 0
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array?.map((el, index) => [el, index])
    stabilizedThis?.sort((a, b) => {
      const orderTable = comparator(a[0], b[0])
      if (orderTable !== 0) {
        return orderTable
      }
      return a[1] - b[1]
    })
    return stabilizedThis?.map((el) => el[0])
  }

  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
    setApiPage(1)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
    if (newPage > page) {
      setApiPage((prev) => prev + 1)
    } else {
      setApiPage((prev) => prev - 1)
    }
  }

  const handleSearch = (value) => {
    setSearch(value)
  }

  const handleEdit = (id) => () => {
    setCurrentId(id)
    handleOpenModal()
  }

  // const onDelete = (id) => () => {
  //   if (!currentId) {
  //     setCurrentId(id)
  //     setDeleteModal(true)
  //   } else {
  //     setDeleteModal(false)
  //     handleDelete(currentId)
  //     setCurrentId()
  //   }
  // }

  const onDelete = (id) => () => {
    if (!currentId) {
      setCurrentId(id)
      setDeleteModal(true)
    } else {
      setDeleteModal(false)
      handleDelete(currentId)
        .unwrap()
        .then(() => {
          console.log('Successfully deleted parking', currentId)
          setCurrentId(null)
        })
        .catch((error) => {
          console.error('Error deleting parking', error)
        })
    }
  }

  const cancelDelete = () => {
    setDeleteModal(false)
    setCurrentId(null)
  }

  const handleTab = (event, newValue) => {
    setTabId(newValue)
  }

  const handleApprove = async () => {
    if (currentList) {
      try {
        await updateList({
          ...currentList,
          is_approved: true,
          is_active: true
        }).unwrap()
        setSelected([])
        setCheckOpen(false)
      } catch (e) {
        console.log(e)
      }
    }
  }

  const cancelCheck = () => {
    setSelected([])
    setCheckOpen(false)
  }

  return (isSuccess && (<>
    <WidgetPaper
      ref={ref}
      title={t('parking_lots')}
    >
      <Box>
        <Tabs
          value={tabId}
          onChange={handleTab}
          scrollButtons={false}
          variant="scrollable"
          // allowScrollButtonsMobile
          aria-label="races select channels"
        >
          <Tab value={1} label={t('active')} key="active" />
          <Tab value={2} label={t('applications')} />
        </Tabs>
        <Divider sx={{ mb: 2 }}/>
        <Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              flex: 1,
              minWidth: 0,
              mb: 2
            }}>
            <SearchInput
              placeholder={t('enter_number')}
              onChange={handleSearch}
              delay={300}
              disabled={!parkingLists.data?.length && !search}
            />
            <Divider orientation="vertical" flexItem />
            <AddButton onClick={handleOpenModal} />
          </Stack>
          <TableContainer>
            <Table>
              <TableHeader
                order={order}
                orderBy={orderBy}
                headLabel={placesDataHead}
                rowCount={lists?.length}
                numSelected={selected.length}
                onRequestSort={handleRequestSort}
                onSelectAllClick={handleSelectAllClick}
              />
              <TableBody>
                {stableSort(lists, getComparator(order, orderBy))?.map(
                  (list, index) => {
                    const isItemSelected =
                      selected.indexOf(list?.number) !== -1
                    const labelId = `enhanced-table-checkbox-${index}`

                    return (
                      <TableRow key={list?.id} hover>
                        {/* ID */}
                        <TableCell>{list?.id}</TableCell>
                        {/* Телефон */}
                        <TableCell>
                          {list.phone || t('no_phone')}
                        </TableCell>
                        {/* Паркоместо */}
                        <TableCell component="th" scope="row">
                          <Typography variant="subtitle2" noWrap>
                            {list?.number}
                          </Typography>
                        </TableCell>
                        {/* Статус */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            color={!list.is_active && 'error'}
                          >
                            {list.is_active ? t('active') : t('inactive')}
                          </Typography>
                        </TableCell>
                        {/* Адрес */}
                        <TableCell>
                          {list.building && list.flat
                            ? `${t('house')} ${list.building}, ${t(
                                'flat'
                              )}${list.flat}`
                            : '-'}
                        </TableCell>
                        {/* Номер автомобиля */}
                        <TableCell>{list.car_number || '-'}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex' }}>
                            <Tooltip title={t('approve')}>
                              <IconButton
                                sx={{ mr: 2 }}
                                id="approve"
                                size="small"
                                color="success"
                                aria-label="approve"
                                onClick={handleCheck(list.id)}
                              >
                                <CheckIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('edit')}>
                              <IconButton
                                sx={{ mr: 2 }}
                                id="menu"
                                size="small"
                                aria-label="edit"
                                onClick={handleEdit(list.id)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('delete')}>
                              <IconButton
                                sx={{ mr: 2 }}
                                id="menu"
                                size="small"
                                aria-label="delete"
                                color="error"
                                onClick={onDelete(list.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                            {/* НОВАЯ кнопка освобождения места */}
                            <Tooltip title={t('free_parking_space')}>
                              <IconButton
                                sx={{ mr: 2 }}
                                id="free-space"
                                size="small"
                                aria-label="free-space"
                                color="primary"
                                onClick={handleFreeSpace(list.id)}
                              >
                                <LocalParkingIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  }
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
        {isSuccess && !parkingLists?.data?.length && (
          <TableEmptyPlaceholder
            icon={LocalParkingIcon}
            message={emptyPlaceholder}
          />
        )}
        {isSuccess && !!parkingLists?.data?.length && (
          <TablePagination
            rowsPerPageOptions={[ 10, 25 ]}
            component="div"
            count={parkingLists?.metadata?.total_count || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Box>
    </WidgetPaper>
    {isLoading && (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    )}
    {/* Новый диалог подтверждения освобождения места */}
    <Dialog open={freeSpaceOpen} fullWidth>
      <DialogTitle>{t('confirmation')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('confirmation_free_space')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancelFreeSpace} color="error">
          {t('cancel')}
        </Button>
        <Button
          color="primary"
          onClick={confirmFreeSpace}
          disabled={isFreeSpaceLoading}
        >
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
    <DialogForm
      open={open}
      currentId={currentId}
      onClose={handleCloseModal}
      buildingId={activeBuildingId}
    />
    <Dialog open={deleteModal} fullWidth>
      <DialogTitle>{t('delete')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('sure_to_delete_item')}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancelDelete}>{t('cancel')}</Button>
        <Button color="error" onClick={onDelete(currentId)}>
          {t('delete')}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog open={checkOpen} fullWidth>
      <DialogTitle>{t('confirmation')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('do_you_really_want_to_approve_this_parking_space')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancelCheck} color="error">
          {t('cancel')}
        </Button>
        <Button color="success" onClick={handleApprove}>
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  </>));
};

export default ParkingPlaces;
